import { type DatabaseJSON } from '@databases/model/database';
import { FieldType } from '@databases/model/field';
import { type AllValueJSON } from '@databases/model/value';

const FILLED_CELL_CHECK_LIMIT = 100;

export function getEntriesCount(database: DatabaseJSON): number {
  return database.entries.length;
}

export function getFieldsCount(database: DatabaseJSON): number {
  return database.fields.length;
}

export function getViewsCount(database: DatabaseJSON): number {
  return database.views.length;
}

export function getFieldTypeCounts(database: DatabaseJSON): Record<FieldType, number> {
  const fieldTypeCounts = new Map<FieldType, number>();

  Object.keys(database.fieldsById).forEach((id) => {
    const type = database.fieldsById[id].type;

    if (fieldTypeCounts.has(type)) {
      fieldTypeCounts.set(type, (fieldTypeCounts.get(type) ?? 0) + 1);
    } else {
      fieldTypeCounts.set(type, 1);
    }
  });

  return Object.fromEntries(fieldTypeCounts) as Record<FieldType, number>;
}

export function getFieldTypes(database: DatabaseJSON): FieldType[] {
  return Array.from(new Set(Object.keys(getFieldTypeCounts(database)))) as FieldType[];
}

/**
 * Determines whether a single cell value holds meaningful content. Empty
 * strings, empty arrays, empty rich text, and unchecked checkboxes are treated
 * as unfilled. Kept conservative so the reported count reflects real content
 * creation rather than default/blank cells.
 */
function isFilledValue(value: AllValueJSON | undefined): boolean {
  if (!value) {
    return false;
  }

  switch (value.type) {
    case FieldType.Text:
    case FieldType.AiSummary:
      return value.value.plain.trim().length > 0;
    case FieldType.Number:
    case FieldType.Date:
      return value.value.trim().length > 0;
    case FieldType.Checkbox:
      return value.value === true;
    case FieldType.Select:
    case FieldType.User:
    case FieldType.Attachment:
    case FieldType.PageLink:
    case FieldType.Hyperlink:
    case FieldType.EntryLink:
    case FieldType.JiraIssue:
      return value.value.length > 0;
    default:
      return false;
  }
}

/**
 * Counts filled (non-empty) cells across persisted entries, ignoring values left
 * behind for deleted fields. Stops after examining 100 cell values. Increasing
 * this limit can cause performance issues for large databases because this runs
 * during database load.
 */
export function getFilledCellsCount(database: DatabaseJSON): number {
  let count = 0;
  let examined = 0;

  for (const entryId of database.entries) {
    const entry = database.entriesById[entryId];

    if (!entry) {
      continue;
    }

    for (const fieldId of Object.keys(entry.values)) {
      if (examined >= FILLED_CELL_CHECK_LIMIT) {
        return count;
      }

      if (!(fieldId in database.fieldsById)) {
        continue;
      }

      examined++;

      if (isFilledValue(entry.values[fieldId])) {
        count++;
      }
    }
  }

  return count;
}
